import type { ReaderPdfTocItem } from "@/interface/DTO_Interfaces"
import { getContentJson } from "@/ts/api/content"

interface PdfOutlineNode {
        title?: string;
        dest?: string | unknown[] | null;
        items?: PdfOutlineNode[];
}

interface PdfOutlineDocument {
        getOutline(): Promise<PdfOutlineNode[] | null>;
        getDestination(destinationName: string): Promise<unknown[] | null>;
        getPageIndex(pageReference: unknown): Promise<number>;
}

const resolveDestination = async (pdf: PdfOutlineDocument, destination: string | unknown[] | null | undefined): Promise<unknown[] | null> => {
        if (typeof destination === "string") {
                return pdf.getDestination(destination)
        }

        return Array.isArray(destination) ? destination : null
}

const resolvePageNumber = async (pdf: PdfOutlineDocument, destination: string | unknown[] | null | undefined): Promise<number | null> => {
        const resolvedDestination = await resolveDestination(pdf, destination)
        const pageReference = resolvedDestination?.[0]

        if (pageReference === undefined || pageReference === null) {
                return null
        }

        return (await pdf.getPageIndex(pageReference)) + 1
}

const buildTocItems = async (pdf: PdfOutlineDocument, nodes: PdfOutlineNode[], parentId: string): Promise<ReaderPdfTocItem[]> => {
        const items: ReaderPdfTocItem[] = []

        for (const [index, node] of nodes.entries()) {
                const title = typeof node.title === "string" ? node.title.trim() : ""
                const children = await buildTocItems(pdf, node.items ?? [], `${parentId}-${index + 1}`)
                const pageNumber = await resolvePageNumber(pdf, node.dest)
                const fallbackPageNumber = children[0]?.pageNumber ?? null

                if (!title || (!pageNumber && !fallbackPageNumber)) {
                        items.push(...children)
                        continue
                }

                items.push({
                        id: `${parentId}-${index + 1}`,
                        title,
                        pageNumber: pageNumber ?? fallbackPageNumber ?? 1,
                        children,
                })
        }

        return items
}

export const loadPdfOutlineToc = async (pdf: PdfOutlineDocument): Promise<ReaderPdfTocItem[]> => {
        const outline = await pdf.getOutline()

        if (!outline) {
                return []
        }

        return buildTocItems(pdf, outline, "pdf-outline")
}

export const loadPdfToc = async (src: string): Promise<ReaderPdfTocItem[]> => {
        const value = await getContentJson<unknown>(src, "Не удалось загрузить оглавление PDF")
        const isItem = (item: unknown): item is ReaderPdfTocItem => {
                if (!item || typeof item !== "object") return false

                const candidate = item as Partial<ReaderPdfTocItem>

                return typeof candidate.id === "string" && typeof candidate.title === "string"
                        && Number.isInteger(candidate.pageNumber) && Number(candidate.pageNumber) > 0
                        && Array.isArray(candidate.children) && candidate.children.every(isItem)
        }

        if (!Array.isArray(value) || !value.every(isItem)) throw new Error("Некорректный формат оглавления PDF")

        return value
}

export const buildPdfFallbackToc = (numPages: number): ReaderPdfTocItem[] =>
        Array.from({ length: numPages }, (_, index) => ({
                id: `page-${index + 1}`,
                title: `Страница ${index + 1}`,
                pageNumber: index + 1,
                children: [],
        }))

export const bundledPdfBookToc: ReaderPdfTocItem[] = [
        {
                id: "chapter-01",
                title: "Глава 1. Введение в программирование",
                pageNumber: 1,
                children: [
                        {
                                id: "section-01",
                                title: "§1. Алгоритмы и исполнители",
                                pageNumber: 1,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-02",
                                title: "§2. Алгоритмические конструкции",
                                pageNumber: 7,
                                pageSide: "left",
                                children: [],
                        },
                        {
                                id: "section-03",
                                title: "§3. Программирование на языке Python",
                                pageNumber: 12,
                                pageSide: "left",
                                children: [],
                        },
                        {
                                id: "section-04",
                                title: "§4. Интегрированная среда разработки",
                                pageNumber: 18,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-05",
                                title: "§5. Выражения",
                                pageNumber: 23,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-06",
                                title: "§6. Ветвления",
                                pageNumber: 31,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-07",
                                title: "§7. Операторы цикла",
                                pageNumber: 37,
                                pageSide: "right",
                                children: [],
                        },
                ],
        },
        {
                id: "chapter-02",
                title: "Глава 2. Алгоритмы обработки данных",
                pageNumber: 42,
                children: [
                        {
                                id: "section-08",
                                title: "§8. Обработка чисел",
                                pageNumber: 42,
                                pageSide: "left",
                                children: [],
                        },
                        {
                                id: "section-09",
                                title: "§9. Обработка наборов данных",
                                pageNumber: 47,
                                pageSide: "left",
                                children: [],
                        },
                        {
                                id: "section-10",
                                title: "§10. Алгоритмы обработки массивов",
                                pageNumber: 54,
                                pageSide: "left",
                                children: [],
                        },
                        {
                                id: "section-11",
                                title: "§11. Итераторы",
                                pageNumber: 61,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-12",
                                title: "§12. Обработка матриц",
                                pageNumber: 66,
                                pageSide: "right",
                                children: [],
                        },
                        {
                                id: "section-13",
                                title: "§13. Обработка символьных данных",
                                pageNumber: 72,
                                pageSide: "left",
                                children: [],
                        },
                ],
        },
]
